import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext, Link } from 'react-router-dom';
import {
  Layers,
  Plus,
  ArrowDownLeft,
  Calendar,
  Building2,
  Scale,
  RefreshCw,
  Eye,
  Paperclip,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Wheat,
  Skull,
  ArrowRightLeft,
  CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { YearSelector } from '@/components/reports/YearSelector';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Combobox } from '@/components/common/Combobox';
import { DocumentAttachmentModal } from '@/components/documents/DocumentAttachmentModal';
import { NewCycleModal } from '@/components/cycles/NewCycleModal';
import { PigArrivalModal } from '@/components/cycles/PigArrivalModal';
import { formatDate, formatNumber } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { cycleService } from '@/services/cycleService';
import { settlementService } from '@/services/settlementService';
import { ProductionCycleRecord, SettlementRecord } from '@/types/database';
import { deriveWorkflowStatus, WorkflowStatus } from '@/lib/workflowStatus';

interface ContextType {
  selectedFarmId: string | null;
}

export const CycleListPage: React.FC = () => {
  const { selectedFarmId } = useOutletContext<ContextType>();
  const { isOperations } = useAuth();
  const navigate = useNavigate();

  // Dynamic Year Selector state
  const [selectedYear, setSelectedYear] = useState<string>('2026');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [operationTypeFilter, setOperationTypeFilter] = useState<string>('');
  const [growerFilter, setGrowerFilter] = useState<string>('');
  const [farmFilter, setFarmFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Modals
  const [showNewCycleModal, setShowNewCycleModal] = useState<boolean>(false);
  const [arrivalModalCycle, setArrivalModalCycle] = useState<ProductionCycleRecord | null>(null);
  const [attachmentCycle, setAttachmentCycle] = useState<ProductionCycleRecord | null>(null);

  // Track expanded rows on desktop & mobile
  const [expandedCycleIds, setExpandedCycleIds] = useState<Set<string>>(new Set());

  const toggleCycleExpansion = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setExpandedCycleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const loadCycles = useCallback(async () => {
    setLoading(true);
    try {
      const [cyclesData, settlementsData] = await Promise.all([
        cycleService.getCycles({
          farmId: selectedFarmId || undefined,
        }),
        settlementService.getSettlements({ status: 'all' }),
      ]);
      setCycles(cyclesData);
      setSettlements(settlementsData);
    } catch (err: any) {
      console.error('Failed to load cycles:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFarmId]);

  useEffect(() => {
    loadCycles();
  }, [loadCycles]);

  const settlementByCycleId = useMemo(() => {
    const map = new Map<string, SettlementRecord>();
    settlements.forEach((s) => {
      if (!s.is_void && s.cycle_id) {
        map.set(s.cycle_id, s);
      }
    });
    return map;
  }, [settlements]);

  // Derived user-facing canonical workflow status: Active -> Draft -> Finalized -> Closed
  const getDerivedCycleStatus = useCallback(
    (c: ProductionCycleRecord): WorkflowStatus => {
      const settle = settlementByCycleId.get(c.id);
      return deriveWorkflowStatus({
        cycleStatus: c.status,
        settlement: settle,
      });
    },
    [settlementByCycleId]
  );

  // Helper to extract batch year of a cycle strictly from batch_code
  const getCycleBatchYear = useCallback((c: ProductionCycleRecord): string | null => {
    if (!c.batch_code) return null;
    const match = c.batch_code.match(/\b(20\d{2})\b/);
    return match ? match[1] : null;
  }, []);

  // Derive available batch years dynamically from Production Cycle batch_code only
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    cycles.forEach((c) => {
      const yr = getCycleBatchYear(c);
      if (yr) yearsSet.add(yr);
    });

    if (yearsSet.size === 0) {
      yearsSet.add('2026');
      yearsSet.add('2025');
      yearsSet.add('2024');
    }

    return Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
  }, [cycles, getCycleBatchYear]);

  // Synchronize default year if current selected is invalid
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // 1. Base cycles filtered by selected Batch Year
  const yearCycles = useMemo(() => {
    if (selectedYear === 'all') return cycles;
    return cycles.filter((c) => getCycleBatchYear(c) === selectedYear);
  }, [cycles, selectedYear, getCycleBatchYear]);

  // Derived filter options for dropdowns
  const growerOptions = useMemo(() => {
    const map = new Map<string, string>();
    yearCycles.forEach((c) => {
      if (c.growers?.id && c.growers?.full_name) {
        map.set(c.growers.id, c.growers.full_name);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [yearCycles]);

  const farmOptions = useMemo(() => {
    const map = new Map<string, string>();
    yearCycles.forEach((c) => {
      if (c.farms?.id && c.farms?.farm_name) {
        map.set(c.farms.id, c.farms.farm_name);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [yearCycles]);

  // 2. Fully filtered dataset
  const filteredCycles = useMemo(() => {
    return yearCycles.filter((c) => {
      const derived = getDerivedCycleStatus(c);
      // Status filter
      if (statusFilter !== 'all' && derived !== statusFilter) {
        return false;
      }

      // Operation Type filter
      if (operationTypeFilter && c.operation_type !== operationTypeFilter) {
        return false;
      }

      // Grower filter
      if (growerFilter && c.growers?.id !== growerFilter) {
        return false;
      }

      // Farm filter
      if (farmFilter && c.farms?.id !== farmFilter) {
        return false;
      }

      // Search filter
      if (search.trim()) {
        const term = search.toLowerCase().trim();
        const code = c.cycle_code?.toLowerCase() || '';
        const batch = c.batch_code?.toLowerCase() || '';
        const grower = c.growers?.full_name?.toLowerCase() || '';
        const farm = c.farms?.farm_name?.toLowerCase() || '';
        if (
          !code.includes(term) &&
          !batch.includes(term) &&
          !grower.includes(term) &&
          !farm.includes(term)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [yearCycles, statusFilter, operationTypeFilter, growerFilter, farmFilter, search, getDerivedCycleStatus]);

  const handleResetFilters = () => {
    setStatusFilter('active');
    setOperationTypeFilter('');
    setGrowerFilter('');
    setFarmFilter('');
    setSearch('');
  };

  // 3. Dynamic Summary Statistics for Top 4 KPI Cards
  const kpiStats = useMemo(() => {
    let activeCyclesCount = 0;
    let draftCyclesCount = 0;
    let finalizedCyclesCount = 0;
    let closedCyclesCount = 0;
    let liveHeadOnFeed = 0;
    let totalPigsDelivered = 0;
    let totalMortality = 0;

    yearCycles.forEach((c) => {
      const delivered = c.headcount?.pigsReceived ?? Number(c.initial_head_count || 0);
      const live = c.headcount?.expectedCurrentHeadcount ?? Number(c.current_head_count || 0);
      const dead = c.headcount?.mortality ?? 0;

      totalPigsDelivered += delivered;
      totalMortality += dead;

      const derived = getDerivedCycleStatus(c);
      if (derived === 'active') {
        activeCyclesCount++;
        liveHeadOnFeed += Math.max(0, live);
      } else if (derived === 'draft') {
        draftCyclesCount++;
      } else if (derived === 'finalized') {
        finalizedCyclesCount++;
      } else if (derived === 'closed') {
        closedCyclesCount++;
      }
    });

    const mortalityRatePct =
      totalPigsDelivered > 0 ? (totalMortality / totalPigsDelivered) * 100 : 0;

    return {
      activeCyclesCount,
      draftCyclesCount,
      finalizedCyclesCount,
      closedCyclesCount,
      liveHeadOnFeed,
      totalPigsDelivered,
      totalMortality,
      mortalityRatePct,
    };
  }, [yearCycles, getDerivedCycleStatus]);

  const handleCycleCreated = (newCycle: any) => {
    loadCycles();
    if (newCycle?.id) {
      navigate(`/cycles/${newCycle.id}`);
    }
  };

  const handleArrivalSuccess = () => {
    loadCycles();
  };

  const isAllYears = selectedYear === 'all';
  const yearTitle = isAllYears ? 'All Years' : selectedYear;

  return (
    <div className="space-y-5 max-w-7xl mx-auto pb-12">
      {/* 1. Standardized PageHeader with Dynamic Year Selector */}
      <PageHeader
        title={`Production Cycles — ${yearTitle}`}
        subtitle="Manage contract growing and company-owned swine production batches across their lifecycle."
        icon={Layers}
        helpScreenKey="cycles"
        actions={
          <>
            <ActionTooltip content="Reload production batches, live headcount, and status filters for this year.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadCycles}
                className="text-xs h-8 sm:h-9 text-slate-700 hover:bg-slate-50 gap-1.5"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            {isOperations && (
              <>
                <ActionTooltip content="Log intake delivery, placement head count, delivery weight, and supplier DR.">
                  <Button
                    size="sm"
                    variant="outline"
                    className="font-semibold gap-1.5 text-xs h-8 sm:h-9 text-emerald-800 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => setArrivalModalCycle({} as ProductionCycleRecord)}
                  >
                    <ArrowDownLeft className="w-3.5 h-3.5" />
                    <span>Log Pig Arrival</span>
                  </Button>
                </ActionTooltip>

                <ActionTooltip content="Open batch setup to assign grower, farm location, and contract split rule.">
                  <Button
                    size="sm"
                    className="bg-emerald-700 hover:bg-emerald-600 font-semibold gap-1.5 text-xs h-8 sm:h-9 shadow-xs text-white"
                    onClick={() => setShowNewCycleModal(true)}
                  >
                    <Plus className="w-3.5 h-3.5 text-emerald-200" />
                    <span>New Production Cycle</span>
                  </Button>
                </ActionTooltip>
              </>
            )}
          </>
        }
        yearSelector={
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        }
      />

      {/* 2. Top-Level Overview: 4 Meaningful KPI Cards */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Active Cycles"
          value={`${kpiStats.activeCyclesCount} Active`}
          subtitle={`${kpiStats.liveHeadOnFeed.toLocaleString()} head live on feed`}
          icon={Layers}
          variant="emerald"
        />
        <KpiCard
          title="Total Pigs Placed"
          value={`${kpiStats.totalPigsDelivered.toLocaleString()} Head`}
          subtitle={isAllYears ? 'All historical arrivals' : `Delivered in ${selectedYear}`}
          icon={ArrowDownLeft}
          variant="default"
        />
        <KpiCard
          title="Mortality Rate"
          value={`${kpiStats.mortalityRatePct.toFixed(2)}%`}
          subtitle={`${kpiStats.totalMortality.toLocaleString()} recorded deaths`}
          icon={Skull}
          variant={kpiStats.mortalityRatePct > 5 ? 'rose' : 'default'}
        />
        <KpiCard
          title="Finalized & Closed"
          value={`${kpiStats.finalizedCyclesCount + kpiStats.closedCyclesCount} Cycles`}
          subtitle={`${kpiStats.finalizedCyclesCount} finalized • ${kpiStats.closedCyclesCount} closed`}
          icon={CheckCircle}
          variant="indigo"
        />
      </KpiCardGroup>

      {/* 3. FilterBar with Status Pills and Comboboxes */}
      <div className="space-y-2">
        {/* Status Lifecycle Segmented Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
          {[
            { key: 'all', label: `All (${yearCycles.length})` },
            { key: 'active', label: `Active (${kpiStats.activeCyclesCount})` },
            { key: 'draft', label: `Draft (${kpiStats.draftCyclesCount})` },
            { key: 'finalized', label: `Finalized (${kpiStats.finalizedCyclesCount})` },
            { key: 'closed', label: `Closed (${kpiStats.closedCyclesCount})` },
          ].map((pill) => (
            <button
              key={pill.key}
              onClick={() => setStatusFilter(pill.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap ${
                statusFilter === pill.key
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {pill.label}
            </button>
          ))}
        </div>

        {/* Search & Filter Pickers */}
        <FilterBar
          searchTerm={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search cycle code, batch, raiser, farm..."
          onReset={handleResetFilters}
          showReset={Boolean(
            search ||
              statusFilter !== 'active' ||
              operationTypeFilter ||
              growerFilter ||
              farmFilter
          )}
        >
          {/* Operation Type Selector */}
          <div className="w-full sm:w-48">
            <Combobox
              options={[
                { value: 'contract_grower', label: 'Contract Growing' },
                { value: 'company_owned', label: 'Company-Owned' },
              ]}
              value={operationTypeFilter}
              onChange={setOperationTypeFilter}
              allowAll={true}
              allLabel="All Operation Types"
              placeholder="Operation Type..."
              searchPlaceholder="Search type..."
            />
          </div>

          {/* Grower / Raiser Combobox */}
          {growerOptions.length > 0 && (
            <div className="w-full sm:w-52">
              <Combobox
                options={growerOptions}
                value={growerFilter}
                onChange={setGrowerFilter}
                allowAll={true}
                allLabel="All Raisers / Growers"
                placeholder="Filter Raiser..."
                searchPlaceholder="Search raiser..."
              />
            </div>
          )}

          {/* Farm Combobox */}
          {farmOptions.length > 0 && (
            <div className="w-full sm:w-48">
              <Combobox
                options={farmOptions}
                value={farmFilter}
                onChange={setFarmFilter}
                allowAll={true}
                allLabel="All Farm Locations"
                placeholder="Filter Farm..."
                searchPlaceholder="Search farm..."
              />
            </div>
          )}
        </FilterBar>
      </div>

      {/* 4. Desktop 8-Column Progressive Disclosure Table & Mobile Cards */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-xs font-medium">
            Loading production cycles...
          </div>
        ) : filteredCycles.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-xs">
            <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="font-semibold text-slate-700">No production cycles found.</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Try adjusting active filters or search terms for {yearTitle}.
            </p>
            {isOperations && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNewCycleModal(true)}
                className="mt-4 text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Create Cycle
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View (md:table, Zero Horizontal Scrolling on 1280px) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse font-sans">
                <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200 select-none">
                  <tr>
                    <th className="py-2.5 px-3 whitespace-nowrap w-36">Cycle & Batch</th>
                    <th className="py-2.5 px-3 min-w-[160px]">Raiser / Grower</th>
                    <th className="py-2.5 px-3 min-w-[140px]">Farm / Pen</th>
                    <th className="py-2.5 px-3 whitespace-nowrap w-28">Placement</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap w-24">Starting</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap w-24">Live Head</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap w-24">Mortality</th>
                    <th className="py-2.5 px-3 text-center whitespace-nowrap w-28">Status</th>
                    <th className="py-2.5 px-3 text-right whitespace-nowrap w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {filteredCycles.map((cycle) => {
                    const isExpanded = expandedCycleIds.has(cycle.id);
                    const pigsDelivered =
                      cycle.headcount?.pigsReceived ?? Number(cycle.initial_head_count || 0);
                    const liveHead =
                      cycle.headcount?.expectedCurrentHeadcount ?? Number(cycle.current_head_count || 0);
                    const deaths = cycle.headcount?.mortality ?? 0;
                    const mortPct = pigsDelivered > 0 ? (deaths / pigsDelivered) * 100 : 0;

                    return (
                      <React.Fragment key={cycle.id}>
                        <tr
                          onClick={() => toggleCycleExpansion(cycle.id)}
                          className={`hover:bg-slate-50/90 cursor-pointer transition-colors ${
                            isExpanded ? 'bg-slate-50 font-medium' : ''
                          }`}
                        >
                          {/* 1. Cycle & Batch */}
                          <td className="py-2.5 px-3 font-mono font-bold text-slate-900 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Link
                                to={`/cycles/${cycle.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-emerald-800 hover:text-emerald-950 hover:underline"
                              >
                                {cycle.cycle_code}
                              </Link>
                              {cycle.batch_code && cycle.batch_code !== cycle.cycle_code && (
                                <span className="text-[10px] text-slate-400 font-normal">
                                  ({cycle.batch_code})
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 2. Raiser / Grower */}
                          <td className="py-2.5 px-3 text-slate-800 font-medium truncate max-w-[180px]">
                            {cycle.growers?.full_name || 'Company Owned'}
                          </td>

                          {/* 3. Farm / Unit */}
                          <td className="py-2.5 px-3 text-slate-600 truncate max-w-[160px]">
                            {cycle.farms?.farm_name || 'Unassigned'}{' '}
                            {cycle.production_units?.unit_code ? `(${cycle.production_units.unit_code})` : ''}
                          </td>

                          {/* 4. Placement Date */}
                          <td className="py-2.5 px-3 font-mono text-slate-600 whitespace-nowrap text-[11px]">
                            {cycle.start_date ? formatDate(cycle.start_date) : '—'}
                          </td>

                          {/* 5. Starting Head */}
                          <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-800 whitespace-nowrap">
                            {formatNumber(pigsDelivered)}
                          </td>

                          {/* 6. Live Head */}
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                            {formatNumber(liveHead)}
                          </td>

                          {/* 7. Mortality % */}
                          <td className="py-2.5 px-3 text-right font-mono text-slate-700 whitespace-nowrap text-[11px]">
                            <span className={mortPct > 5 ? 'text-rose-700 font-bold' : ''}>
                              {mortPct.toFixed(1)}% ({deaths})
                            </span>
                          </td>

                          {/* 8. Status Badge */}
                          <td className="py-2.5 px-3 text-center whitespace-nowrap">
                            <StatusBadge status={getDerivedCycleStatus(cycle)} category="cycle" />
                          </td>

                          {/* 9. Actions Toggle */}
                          <td className="py-2.5 px-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <ActionTooltip content="Open full batch workspace, feed deliveries, sales, and health log.">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/cycles/${cycle.id}`)}
                                  className="h-7 text-xs px-2 text-slate-700 hover:bg-slate-100 border-slate-300"
                                >
                                  View
                                </Button>
                              </ActionTooltip>
                              <ActionTooltip content={isExpanded ? 'Collapse cohort details.' : 'Expand cohort breakdown and contract details.'}>
                                <button
                                  onClick={(e) => toggleCycleExpansion(cycle.id, e)}
                                  className="p-1 text-slate-400 hover:text-slate-700"
                                  aria-label="Toggle details"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-slate-700" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                              </ActionTooltip>
                            </div>
                          </td>
                        </tr>

                        {/* Progressive Disclosure Detail Panel */}
                        {isExpanded && (
                          <tr className="bg-slate-50/95 border-b border-slate-200">
                            <td colSpan={9} className="p-4">
                              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-inner space-y-3 text-xs">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-slate-900 text-sm">
                                      {cycle.cycle_code} — {cycle.growers?.full_name || 'Company Owned'}
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-100 text-slate-700">
                                      Batch: {cycle.batch_code || cycle.cycle_code}
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                      {cycle.operation_type === 'contract_grower' ? 'Contract Growing' : 'Company-Owned'}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1.5">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setAttachmentCycle(cycle)}
                                      className="h-7 text-xs px-2 text-slate-700 border-slate-300 gap-1"
                                    >
                                      <Paperclip className="w-3.5 h-3.5 text-emerald-700" />
                                      <span>Attached Slips</span>
                                    </Button>
                                    <Link
                                      to={`/cycles/${cycle.id}`}
                                      className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-700 hover:bg-emerald-600 text-white rounded text-xs font-semibold transition-colors"
                                    >
                                      <span>Open Cycle Workspace</span>
                                      <ExternalLink className="w-3 h-3 ml-0.5" />
                                    </Link>
                                  </div>
                                </div>

                                {/* Reactive Headcount Trace */}
                                <div className="space-y-1.5">
                                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                                    Reactive Headcount Trace
                                  </span>
                                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-[11px] font-mono">
                                    <div>
                                      <span className="text-[10px] text-slate-400 block font-sans">1. Received</span>
                                      <span className="font-bold text-slate-900">
                                        +{cycle.headcount?.pigsReceived ?? pigsDelivered}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-[10px] text-slate-400 block font-sans">2. Transfers In</span>
                                      <span className="font-bold text-emerald-700">
                                        +{cycle.headcount?.transfersIn ?? 0}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-[10px] text-slate-400 block font-sans">3. Mortalities</span>
                                      <span className="font-bold text-rose-700">
                                        -{cycle.headcount?.mortality ?? deaths}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-[10px] text-slate-400 block font-sans">4. Transfers Out</span>
                                      <span className="font-bold text-amber-700">
                                        -{cycle.headcount?.transfersOut ?? 0}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-[10px] text-slate-400 block font-sans">5. Pigs Sold</span>
                                      <span className="font-bold text-indigo-700">
                                        -{cycle.headcount?.pigsSold ?? 0}
                                      </span>
                                    </div>
                                    <div className="border-l border-slate-200 pl-2">
                                      <span className="text-[10px] text-slate-400 block font-sans font-bold">Live On Feed</span>
                                      <span className="font-bold text-slate-900 text-xs">
                                        {liveHead} head
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Contract & Facility Metadata */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] pt-1">
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Farm Facility</span>
                                    <span className="text-slate-800">{cycle.farms?.farm_name || 'Unassigned'}</span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Unit / Barn Code</span>
                                    <span className="font-mono text-slate-800">
                                      {cycle.production_units?.unit_code || '—'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Contract Split</span>
                                    <span className="font-mono text-slate-800">
                                      {cycle.grower_contracts
                                        ? `${cycle.grower_contracts.vsb_share_pct}% VSB / ${cycle.grower_contracts.grower_share_pct}% Grower`
                                        : 'Company-Owned (100% VSB)'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Reconciliation Status</span>
                                    <span className="font-medium text-slate-800">
                                      {cycle.reconciliation_status === 'needs_review' ? (
                                        <span className="text-amber-700 font-bold">Needs Review</span>
                                      ) : cycle.reconciliation_status === 'historical_incomplete_accepted' ? (
                                        <span className="text-slate-600">Historical Accepted</span>
                                      ) : (
                                        <span className="text-emerald-700 font-bold">Reconciled Clean</span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Touch-First Responsive Cards (< md, Zero Horizontal Scroll) */}
            <div className="md:hidden divide-y divide-slate-200">
              {filteredCycles.map((cycle) => {
                const isExpanded = expandedCycleIds.has(cycle.id);
                const pigsDelivered =
                  cycle.headcount?.pigsReceived ?? Number(cycle.initial_head_count || 0);
                const liveHead =
                  cycle.headcount?.expectedCurrentHeadcount ?? Number(cycle.current_head_count || 0);
                const deaths = cycle.headcount?.mortality ?? 0;
                const mortPct = pigsDelivered > 0 ? (deaths / pigsDelivered) * 100 : 0;

                return (
                  <div
                    key={cycle.id}
                    onClick={() => toggleCycleExpansion(cycle.id)}
                    className={`p-3.5 space-y-2.5 cursor-pointer transition-colors ${
                      isExpanded ? 'bg-slate-50/80' : 'bg-white'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 font-mono font-bold text-xs text-slate-900">
                        <span>{cycle.cycle_code}</span>
                        {cycle.batch_code && cycle.batch_code !== cycle.cycle_code && (
                          <span className="text-[10px] text-slate-400">({cycle.batch_code})</span>
                        )}
                      </div>
                      <StatusBadge status={getDerivedCycleStatus(cycle)} category="cycle" />
                    </div>

                    {/* Identity & Location */}
                    <div className="space-y-1 text-xs text-slate-600">
                      <p className="font-bold text-slate-900">{cycle.growers?.full_name || 'Company Owned'}</p>
                      <div className="flex items-center justify-between text-[11px]">
                        <span>
                          {cycle.farms?.farm_name || 'Farm'}{' '}
                          {cycle.production_units?.unit_code ? `(${cycle.production_units.unit_code})` : ''}
                        </span>
                        <span className="font-mono text-slate-500">
                          {cycle.start_date ? formatDate(cycle.start_date) : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Headcount Metrics Grid */}
                    <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2 rounded-lg border border-slate-100 text-center font-mono text-xs">
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Placed</span>
                        <span className="font-semibold text-slate-800">{pigsDelivered}</span>
                      </div>
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Live Head</span>
                        <span className="font-bold text-slate-900">{liveHead}</span>
                      </div>
                      <div>
                        <span className="text-[9px] font-bold text-slate-400 uppercase block font-sans">Mortality</span>
                        <span className={`font-semibold ${mortPct > 5 ? 'text-rose-700' : 'text-slate-700'}`}>
                          {mortPct.toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    {/* Expanded Drawer on Mobile */}
                    {isExpanded && (
                      <div className="pt-2.5 border-t border-slate-200 space-y-2 text-[11px] bg-white p-3 rounded-lg border border-slate-100">
                        <div className="space-y-1 text-slate-600">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Operation:</span>
                            <span className="font-medium text-slate-800 capitalize">
                              {cycle.operation_type === 'contract_grower' ? 'Contract Growing' : 'Company-Owned'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Transfers In / Out:</span>
                            <span className="font-mono text-slate-800">
                              +{cycle.headcount?.transfersIn ?? 0} / -{cycle.headcount?.transfersOut ?? 0}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Pigs Sold:</span>
                            <span className="font-mono text-slate-800">
                              {cycle.headcount?.pigsSold ?? 0} head
                            </span>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentCycle(cycle);
                            }}
                            className="h-8 text-xs text-slate-700"
                          >
                            <Paperclip className="w-3.5 h-3.5 mr-1 text-emerald-700" /> Slips
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/cycles/${cycle.id}`);
                            }}
                            className="h-8 text-xs bg-emerald-700 hover:bg-emerald-600 text-white font-semibold"
                          >
                            Open Workspace →
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <NewCycleModal
        isOpen={showNewCycleModal}
        onClose={() => setShowNewCycleModal(false)}
        onSuccess={handleCycleCreated}
        defaultFarmId={selectedFarmId}
      />

      {arrivalModalCycle && (
        <PigArrivalModal
          isOpen={arrivalModalCycle !== null}
          onClose={() => setArrivalModalCycle(null)}
          onSuccess={handleArrivalSuccess}
          cycleId={arrivalModalCycle.id}
          cycleCode={arrivalModalCycle.cycle_code}
        />
      )}

      {attachmentCycle && (
        <DocumentAttachmentModal
          isOpen={attachmentCycle !== null}
          onClose={() => setAttachmentCycle(null)}
          entityType="production_cycle"
          entityId={attachmentCycle.id}
          entityTitle={`Documents for ${attachmentCycle.cycle_code}`}
          defaultDocumentType="weighing_sheet"
        />
      )}
    </div>
  );
};
